import React, {useState, useCallback, useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {submitStreetZipComment} from "../../services/rating";
import {getDataBaseAddressWithStreetZip} from "../../services/rating";
import {LoadScript, Autocomplete} from "@react-google-maps/api";
import {GoogleAddress} from "../GoogleAddress";
import {sweetAlertQuestion, sweetAlerts} from "../../utils/sweet-alerts";
import axios from "axios";
import {getContractorDashboardService} from "../../services/contractorServices";
import InlineGrammarChecker from "../../CustomComponents/InlineGrammarChecker";
const {extractStreetAddressWithGoogle, extractStreetAddressFallback} = require("../../services/geocoding");

const API_URL = process.env.REACT_APP_API_URL;

const SubmitComment = () => {
	const navigate = useNavigate();
	const [formData, setFormData] = useState({
		commentAddress: "",
		commentDate: "",
		commentText: "",
		commentType: false,
		addresses: "",
	});

	const [searchType, setSearchType] = useState("street"); // 'street' or 'zipcode'

	const [searchResults, setSearchResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errors, setErrors] = useState({});
	const [showCustomDropdown, setShowCustomDropdown] = useState(false);
	const [address, setAddress] = useState("");
	const [customResults, setCustomResults] = useState([]);
	const autocompleteRef = useRef(null);
	const [checkAddress, setCheckAddress] = useState(false);
	const [addressFlag, setAddressFlag] = useState("");
	const [limits, setLimits] = useState({});

	const [addressData, setAddressData] = useState({});
	const [languageToolErrors, setLanguageToolErrors] = useState([]);
	const [isCheckingLanguage, setIsCheckingLanguage] = useState(false);
	const languageToolTimeoutRef = useRef(null);

	const getData = async () => {
		try {
			const response = await getContractorDashboardService({});
			setLimits(response?.data);
		} catch (error) {}
	};

	useEffect(() => {
		getData();
	}, []);

	const searchCommentSites = async (address) => {
		if (address.length < 3) {
			setSearchResults([]);
			return;
		}

		setIsSearching(true);

		try {
			const response = await getDataBaseAddressWithStreetZip(address);

			if (response.data.data && response.data.data.length > 0) {
				// Transform the API response to match your expected format
				const transformedResults = response.data.data.map((item, index) => ({
					id: item.addresses_id || index + 1,
					address: item.addresses,
					verified: true,
				}));

				setSearchResults(transformedResults);
				setShowCustomDropdown(true);
			} else {
				setSearchResults([]);
				setShowCustomDropdown(false);
			}
		} catch (error) {
			console.error("Database search failed", error);
			// setCustomResults([{ full_address: 'Error searching in database.' }]);
		}

		// Simulate Google Places API call
		setTimeout(() => {
			setIsSearching(false);
		}, 500);
	};

	const checkLanguageTool = useCallback((text) => {
		if (!text || text.trim().length < 3) {
			setLanguageToolErrors([]);
			return;
		}

		// Clear previous timeout
		if (languageToolTimeoutRef.current) {
			clearTimeout(languageToolTimeoutRef.current);
		}

		// Debounce the API call
		languageToolTimeoutRef.current = setTimeout(async () => {
			setIsCheckingLanguage(true);
			try {
				const response = await fetch("https://api.languagetool.org/v2/check", {
					method: "POST",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: new URLSearchParams({
						text: text,
						language: "en-US",
					}),
				});

				const data = await response.json();
				setIsCheckingLanguage(false);

				if (data && data.matches) {
					setLanguageToolErrors(data.matches);
				} else {
					setLanguageToolErrors([]);
				}
			} catch (error) {
				console.error("LanguageTool error:", error);
				setIsCheckingLanguage(false);
				setLanguageToolErrors([]);
			}
		}, 1000); // 1 second debounce
	}, []);

	useEffect(() => {
		return () => {
			if (languageToolTimeoutRef.current) {
				clearTimeout(languageToolTimeoutRef.current);
			}
		};
	}, []);

	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}

		// Check grammar for comment text
		if (field === "commentText") {
			checkLanguageTool(value);
		}
	};

	const handleAddressSelect = (address) => {
		setFormData((prev) => ({
			...prev,
			commentAddress: address,
		}));
		setSearchResults([]);

		if (errors.commentAddress) {
			setErrors((prev) => ({
				...prev,
				commentAddress: null,
			}));
		}
	};

	const validateForm = () => {
		const newErrors = {};

		// Address validation
		if (!formData.commentAddress.trim()) {
			newErrors.commentAddress = "Comment address is required";
		}

		// Date validation
		if (!formData.commentDate) {
			newErrors.commentDate = "Comment date is required";
		} else {
			const selectedDate = new Date(formData.commentDate);
			const today = new Date();
			if (selectedDate > today) {
				newErrors.commentDate = "Comment date cannot be in the future";
			}
		}

		// Comment text validation
		if (!formData.commentText.trim()) {
			newErrors.commentText = "Comment text is required";
		} else if (formData.commentText.trim().length < 10) {
			newErrors.commentText = "Comment must be at least 10 characters long";
		} else if (formData.commentText.trim().length > 1000) {
			newErrors.commentText = "Comment cannot exceed 1000 characters";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const calculateCommentDateStatus = () => {
		if (!formData.commentDate) return null;

		const commentDate = new Date(formData.commentDate);
		const today = new Date();
		const daysDiff = Math.floor((today - commentDate) / (1000 * 60 * 60 * 24));

		if (daysDiff <= 90) {
			return {status: "fresh", text: "Fresh Comment", color: "success", days: daysDiff, type: "fresh"};
		} else {
			return {status: "historic", text: "Historic Comment", color: "warning", days: daysDiff, type: "historic"};
		}
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);

		try {
			// In real app, this would be an API call like: const response = await submitComment(formData);

			// Simulate API call
			await new Promise((resolve) => setTimeout(resolve, 2000));

			const dateStatus = calculateCommentDateStatus();
			let status = false;
			if (dateStatus?.type === "historic") {
				status = true;
			}

			const requestBody = {
				description: formData.commentText,
				comment_date: formData.commentDate,
				comment_type: formData.commentType,
				full_addresses: formData.commentAddress,
				addresses: formData.addresses,
				is_historic: status,

				route: addressData.route,
				locality: addressData.locality,
				sub_locality: addressData.sublocality,
				postal_code: addressData.postalcode,
				addressFlag: addressFlag,
			};

			const response = await submitStreetZipComment(requestBody);

			if (response.data && response.status === true) {
				setLanguageToolErrors([]);
				sweetAlerts("success", "Comment submitted successfully!");
				navigate("/contractor-dashboard/comments");
			} else {
				sweetAlerts("error", response.message || "Failed submitting comment. Please try again.");
			}
		} catch (error) {
			console.error("Error submitting comment:", error);
			sweetAlerts("error", error.message, "Failed submitting comment. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const isFormValid = () => {
		return formData.commentAddress.trim() && formData.commentDate && formData.commentText.trim().length >= 10;
	};

	const commentDateStatus = calculateCommentDateStatus();

	const dropdownRef = useRef(null);

	const handlePlaceSelect = async () => {
		const place = autocompleteRef.current.getPlace();
		if (place.geometry) {
			const address = place.formatted_address;
			setAddress(address);
			setCheckAddress(true);

			handleInputChange("commentAddress", address);
		}
	};

	const handleInputBlur = async (address) => {
		setCheckAddress(true);
		if (!address) {
			return null;
		}
		const googleResult = await extractStreetAddressWithGoogle(address, "zip_code");

		setAddressData(googleResult?.components);

		if (googleResult.success && googleResult.postalCode) {
			setFormData((prev) => ({
				...prev,
				addresses: formData.commentType ? googleResult.postalCode : googleResult.streetAddress,
			}));
			setShowCustomDropdown(false);
			return;
		}

		if (formData.commentType) {
			return extractZipcodeManual(address);
		} else {
			const newstreet = extractStreetAddressFallback(address);

			setFormData((prev) => ({
				...prev,
				addresses: newstreet,
			}));
			setShowCustomDropdown(false);
		}
	};

	// Enhanced manual zipcode extraction function
	const extractZipcodeManual = (fullAddress) => {
		if (!fullAddress) return "";

		// Look for 6-digit zipcode pattern (common in India and some other countries)
		const sixDigitMatch = fullAddress.match(/\b\d{6}\b/);
		if (sixDigitMatch) {
			setFormData((prev) => ({
				...prev,
				addresses: sixDigitMatch[0],
			}));
			return sixDigitMatch[0];
		}

		// Look for 5-digit zipcode pattern (most common US format)
		const fiveDigitMatch = fullAddress.match(/\b\d{5}\b/);
		if (fiveDigitMatch) {
			setFormData((prev) => ({
				...prev,
				addresses: fiveDigitMatch[0],
			}));
			return fiveDigitMatch[0];
		}

		// If no 5 or 6-digit zipcode found, try to find any zipcode-like pattern
		const zipcodePattern = fullAddress.match(/\b\d{3,6}(?:-\d{4})?\b/);
		if (zipcodePattern) {
			setFormData((prev) => ({
				...prev,
				addresses: zipcodePattern[0],
			}));
			return zipcodePattern[0];
		}

		// If still no zipcode found, try to extract the last numeric part that might be a zipcode
		const lastNumericPart = fullAddress.match(/\b\d+\s*$/);
		if (lastNumericPart) {
			setFormData((prev) => ({
				...prev,
				addresses: lastNumericPart[0].trim(),
			}));
			return lastNumericPart[0].trim();
		}

		setFormData((prev) => ({
			...prev,
			addresses: "",
		}));
		return "";
	};

	const handleManualSearch = async () => {
		const place = autocompleteRef.current?.getPlace();

		// Only call your DB if user typed something but didn't select a Google suggestion
		if (!place || !place.formatted_address) {
			setShowCustomDropdown(true);
			try {
				const res = await axios.get(`${API_URL}/api/custom-address-search`, {
					params: {query: address},
				});
				if (res.data && res.data?.data && res.data?.data.length > 0) {
					setCustomResults(res.data?.data); // Show DB results
				}
			} catch (error) {
				console.error("Database search failed", error);
				setCustomResults([{full_address: "Error searching in database."}]);
			}
		}
	};

	const handleCustomSelect = (selectedAddress) => {
		setAddress(selectedAddress);
		setCustomResults([]);
		setShowCustomDropdown(false);
	};

	return (
		<div className="dashboard-page">
			<div className="page-header">
				<h2>Submit Comment</h2>
				<p>Share your experience and help other contractors make informed decisions</p>
			</div>

			{/* Form Container */}
			<div className="form-container">
				<form
					onSubmit={(e) => {
						e.preventDefault();
						handleSubmit();
					}}>
					<div className="form-section">
						<h3>Comment Information</h3>

						{/* Search Type Toggle */}
						<div className="form-group">
							<label className="form-label">Search Type</label>
							<div className="search-type-toggle">
								<button
									type="button"
									className={`toggle-btn ${searchType === "street" ? "active" : ""}`}
									onClick={(e) => {
										setSearchType("street");
										setFormData({
											commentType: false,
											commentAddress: "",
											commentDate: "",
											commentText: "",
											addresses: "",
										});
										setAddress("");
										setCheckAddress(false);
									}}>
									Street Address
								</button>
								<button
									type="button"
									className={`toggle-btn ${searchType === "zipcode" ? "active" : ""}`}
									onClick={(e) => {
										setSearchType("zipcode");
										setFormData({
											commentType: true,
											commentAddress: "",
											commentDate: "",
											commentText: "",
											addresses: "",
										});
										setAddress("");
										setCheckAddress(false);
									}}>
									Zipcode
								</button>
							</div>
						</div>

						{/* select address zipcode or street */}
						{checkAddress && (
							<div className="form-group">
								<label className="form-label">
									you are submitting a comment for a {formData.commentType ? "zipcode" : "street"} "{formData.addresses}"
								</label>
							</div>
						)}

						{/* Search */}
						<div className="form-group" style={{position: "relative"}} ref={dropdownRef}>
							<label className="form-label">
								Search *<span className="field-hint">Google verified addresses only</span>
							</label>
							<div className="search-input-container">
								{/* <input
                    type="text"
                    className={`form-input ${errors.commentAddress ? 'error' : ''}`}
                    placeholder={searchType === 'street' ? "Enter street address..." : "Enter zipcode..."}
                    value={formData.commentAddress}
                    onChange={(e) => {
                      handleInputChange('commentAddress', e.target.value);
                      searchCommentSites(e.target.value);
                      setShowCustomDropdown(false);
                    }}
                  /> */}
								{/* <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}> */}
								{/* <div style={{ position: 'relative' }}> */}
								{/* <Autocomplete
                            onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                            onPlaceChanged={handlePlaceSelect}
                          >
                            <input
                              type="text"
                              className={`form-input ${errors.commentAddress ? 'error' : ''}`}
                              placeholder="Enter an address (e.g. 123 Main St, City, State)"
                              value={address}
                              onChange={(e) => {
                                const value = e.target.value;
                                setAddress(value);
                                handleInputChange('commentAddress', e.target.value);
                                setCustomResults([]); // Clear previous
                                searchCommentSites(e.target.value);
                                setShowCustomDropdown(false);
                                if (value.length > 2) {
                                  handleManualSearch(value); // Query DB when typing
                                }
                              }}
                              onBlur={(e) => {
                                // This function will be called when input loses focus
                                handleInputBlur(e.target.value);
                              }}
                            />
                          </Autocomplete> */}

								{/* Custom Dropdown */}
								{/* {showCustomDropdown && searchResults.length > 0 && (
                            <div className="tz-pac-container" style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                zIndex: 1000,
                                maxHeight: '145px',
                                backgroundColor: '#fff',
                                borderRadius: '2px',
                                borderTop: '1px solid #d9d9d9',
                                fontFamily: 'Arial, sans-serif',
                                boxShadow: '0 2px 6px rgba(0, 0, 0, .3)',
                                boxSizing: 'border-box',
                                overflowY: 'auto',
                              }}>
                                {customResults.map((item, index) => {
                                  const [main, ...rest] = item.full_address.split(' ');
                                  const subtext = item.full_address.replace(main, '').trim();

                                  return (
                                    <div class="tz-pac-item"
                                      key={index}
                                      onClick={() => handleCustomSelect(item.full_address)}
                                    >
                                      <span class="tz-pac-icon tz-pac-icon-marker"></span>
                                      <span class="tz-pac-item-query">
                                        <span class="tz-pac-matched">{main}</span>
                                        </span>
                                        <span>{subtext}</span>
                                      </div>
                                  );
                                })}
                              <div class="pac-item"><span class="pac-icon pac-icon-marker"></span><span class="pac-item-query"><span class="pac-matched">123</span> <span class="pac-matched">Main</span> Street</span><span>White Plains, NY, USA</span></div>
                              <div class="pac-item"><span class="pac-icon pac-icon-marker"></span><span class="pac-item-query"><span class="pac-matched">123</span> <span class="pac-matched">Main</span> Street</span><span>White Plains, NY, USA</span></div>
                            </div>
                          )} */}
								{/* </div> */}
								{/* </LoadScript> */}
								{/* <GoogleAddress
									address={formData.commentAddress}
									callBackAction={(val) => {
										setFormData((prev) => ({...prev, commentAddress: val}));
										handleInputBlur(val);
									}}
								/> */}
								<GoogleAddress
									styleFlag="dashboardStyle"
									address={formData.commentAddress}
									callBackAction={(val) => {
										if (val.value) {
											setFormData((prev) => ({...prev, commentAddress: val.value}));
											handleInputBlur(val.value);
											setAddressFlag(val.type);
										}
									}}
								/>

								{/* {isSearching && (
                      <div className="search-loading">
                        <div className="spinner"></div>
                      </div>
                    )} */}
							</div>
							{errors.commentAddress && <div className="error-message">{errors.commentAddress}</div>}
						</div>

						{formData.commentType && (
							<div className="form-group">
								<label className="form-label">
									Zipcode *<span className="field-hint"></span>
								</label>
								<input className={`form-input ${!formData.addresses ? "error" : ""}`} value={formData.addresses} onChange={(e) => setFormData((prev) => ({...prev, addresses: e.target.value}))} />

								{!formData.addresses && <div className="error-message">Zip is required</div>}
							</div>
						)}

						{/* Comment Date */}
						<div className="form-group">
							<label className="form-label">
								Comment Date *<span className="field-hint">When did you work at this job site?</span>
							</label>
							<input
								type="date"
								onClick={(e) => {
									e.target.showPicker && e.target.showPicker();
								}}
								className={`form-input ${errors.commentDate ? "error" : ""}`}
								value={formData.commentDate}
								onChange={(e) => handleInputChange("commentDate", e.target.value)}
								max={new Date().toISOString().split("T")[0]}
							/>
							{errors.commentDate && <div className="error-message">{errors.commentDate}</div>}

							{/* Date Status Info */}
							{commentDateStatus && (
								<div className={`date-status ${commentDateStatus.type}`}>
									{commentDateStatus.type === "fresh" ? (
										<div className="status-fresh">
											<span className="status-icon">✓</span>
											<span>Fresh comment ({commentDateStatus.days} days ago)</span>
										</div>
									) : (
										<div className="status-historic">
											<span className="status-icon">⚠️</span>
											<span>Historic comment ({commentDateStatus.days} days ago) - This address will be flagged with "Previous History"</span>
										</div>
									)}
								</div>
							)}
						</div>

						{/* Comment Text */}
						<div className="form-group">
							<label className="form-label">
								Comment Text *<span className="field-hint">Describe your experience with this job site</span>
							</label>
							<textarea
								className={`form-textarea ${errors.commentText ? "error" : ""}`}
								value={formData.commentText}
								onChange={(e) => handleInputChange("commentText", e.target.value)}
								placeholder="Describe your experience with this job/street/zipcode. Be specific about what went well and what could have been better…"
								rows="6"
								maxLength="1000"
							/>
							<div className="character-count">{formData.commentText.length}/1000 characters</div>
							{errors.commentText && <div className="error-message">{errors.commentText}</div>}
						</div>

						{/* Submit Button */}
						<div className="form-submit">
							<button type="submit" className="btn btn-primary btn-large" disabled={!isFormValid() || isSubmitting}>
								{isSubmitting ? (
									<>
										<div className="spinner-small"></div>
										Submitting Comment...
									</>
								) : (
									"Submit Comment"
								)}
							</button>
						</div>

						{/* Form Stats */}
						<div className="form-stats">
							<div className="stats-item">
								<strong>Today:</strong> {limits?.totalDailyComment}/15 Comment
							</div>
							<div className="stats-item">
								<strong>Total:</strong> {limits?.totalComment}/100 Comment
							</div>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default SubmitComment;
